import {ChallengeMode, EducationSessionErrorReason, EducationSessionPassedResult} from "../../../schema";
import {WithoutTypename} from "../../../types";
import {DefaultAdaptiveInput, PlayableAction, PlayerReduxDispatch, PlayerReduxState} from "../types";
import {analyticsSendEvent, toEventData} from "../../../libs/analytics";
import {getCurrentScenario, getLevel} from "../selectors";
import {_next, getBranch, getBranchActions} from "../utils";

type props = {
  result: WithoutTypename<EducationSessionPassedResult>;
  state: PlayerReduxState;
  dispatch: PlayerReduxDispatch;
  action: PlayableAction;
  challengeMode?: ChallengeMode;
  fromTimer?: boolean;
  uuid?: string;
};

export default function handleUserAnswerResult({result, state, dispatch, action,
                                                 challengeMode, fromTimer, uuid}: props) {
  const errorState = result.passed === false ? result.errorReason : null;
  const branchIndex = result.branchId ? -1 : undefined;
  // Mic button stays disabled while isRecognizing; only true when adaptive feedback is in flight (has inputId).
  const isRecognizing =
    !!result.inputId &&
    result.passed !== false &&
    ((challengeMode === ChallengeMode.COMPETENCE_TEST && result.waitForFeedback) ||
      challengeMode === ChallengeMode.RPG_MODE);

  dispatch({
    type: "update",
    payload: {
      isRecognizing: isRecognizing,
      lastUserInputResult: {
        ok: result.passed !== false,
        recognizedText: result.recognizedText ?? undefined,
        audioUuid: uuid,
        errorState: errorState ?? null,
      },
      branchId: result.branchId ?? undefined,
      branchIndex,
      adaptiveInput: (() => {
        if (result.inputId)
          if (
            (challengeMode === ChallengeMode.COMPETENCE_TEST && result.waitForFeedback) ||
            challengeMode === ChallengeMode.RPG_MODE
          )
            return {...DefaultAdaptiveInput, id: result.inputId};

        return null;
      })(),
    },
  });
  if (!result.passed && errorState === EducationSessionErrorReason.NO_ANSWER) {
    if (challengeMode === ChallengeMode.RPG_MODE) {
      dispatch({
        type: "update",
        payload: {rpgTurnKey: state.rpgTurnKey + 1, isRecognizing: false, adaptiveInput: null}
      });
      return;
    }
    if (fromTimer) {
      dispatch({type: "next"});
      return;
    }
  }

  if (
    challengeMode === ChallengeMode.RPG_MODE &&
    !result.passed &&
    errorState === EducationSessionErrorReason.DAILO_STT_ERROR
  ) {
    dispatch({type: "update", payload: {rpgTurnKey: state.rpgTurnKey + 1, isRecognizing: false, adaptiveInput: null}});
    return;
  }

  const resultQueue: Array<"notPassed" | "branchSelect" | "passed" | "scenarioTrigger" | "interrupt" | "adaptive"> =
    [];
  if (result.interrupt) {
    dispatch({
      type: "interrupt",
      payload: {
        interruptReason: result.errorReason,
      },
    });
  } else {
    if (challengeMode === ChallengeMode.COMPETENCE_TEST)
      if (result.passed && !result.waitForFeedback) {
        dispatch({type: "next"});
        return;
      }

    if (challengeMode !== ChallengeMode.COMPETENCE_TEST) {
      const dispatchStartScenario = (scenarioId: string, branchId?: string) => {
        dispatch({
          type: "startScenario",
          payload: {scenarioId, branchId},
        });
      };

      const dispatchPassOrNotPass = (result: any) => {
        if (result.passed)
          dispatch({
            type: "next",
          });
        else if (result.passed === false) {
          dispatch({
            type: "notPassed",
          });
          if (!errorState && result.branchId)
            dispatch({
              type: "next",
            });
        } else {
          // passed === null check rpg mode
          if (challengeMode !== ChallengeMode.RPG_MODE || !result.inputId)
            console.error("passed = None разрешено только для RPG MODE");
        }
      };

      if (result.branchId) {
        const rootAction = getCurrentScenario(state).actions[state.index];
        const branch = rootAction ? getBranch(rootAction, result.branchId) : undefined;
        const branchActions = branch ? (getBranchActions(branch) ?? []) : [];
        const nextBranchIndex = branchActions ? _next(branchActions, state.level, branchIndex!) : undefined;
        if (result.scenarioId) {
          if (result.scenarioId !== branch?.targetScenarioId)
            // trigger scenario by trigger phrase
            dispatchStartScenario(result.scenarioId);
          else if (nextBranchIndex === undefined) {
            if (result.passed) dispatchStartScenario(result.scenarioId, result.branchId);
            else if (result.passed === false) {
              dispatch({
                type: "notPassed",
              });
              if (!errorState) dispatchStartScenario(result.scenarioId, result.branchId);
            } else {
              // passed === null check rpg mode
              if (challengeMode !== ChallengeMode.RPG_MODE || !result.inputId)
                console.error("passed = None разрешено только для RPG MODE");
            }
          } else dispatchPassOrNotPass(result);
        } else dispatchPassOrNotPass(result);
      } else {
        if (result.scenarioId) dispatchStartScenario(result.scenarioId);
        else dispatchPassOrNotPass(result);
      }
    }
  }

  if (result.interrupt) {
    resultQueue.push("interrupt");
  } else {
    if (challengeMode !== ChallengeMode.COMPETENCE_TEST && challengeMode !== ChallengeMode.RPG_MODE) {
      if (result.branchId) resultQueue.push("branchSelect");
      if (result.scenarioId) resultQueue.push("scenarioTrigger");
      if (result.passed) resultQueue.push("passed");
      else resultQueue.push("notPassed");
    } else {
      if (result.inputId) resultQueue.push("adaptive");
    }
  }

  analyticsSendEvent("playerPassActionResult", {
    sessionId: state.sessionId!,
    level: getLevel(state),
    actionId: action.id,
    actionType: action.__typename,
    inputId: result.inputId ?? "",
    result: resultQueue.join(","),
    ...toEventData(result),
  });

  if (resultQueue.length === 0) {
    state.raiseProblem && state.raiseProblem("userInputNotPassed");
  }
}
